import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { Input, Spinner } from 'reactstrap';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTicketDetails } from '../../../pages/Tickets/context/TicketDetailsProvider';
import TicketResponseList from '../TicketResponse/TicketResponseList';
import { FileUtil, FormValidationUtil } from '../../../utils';
import { createTicketResponse } from '../../../store/apis/ticketService';
import sendIcon from '../../../assets/icons/sendicon.png';
import attachDocIcon from '../../../assets/icons/attachdocicon.png';
import SelectedFiles from '../../Common/SelectedFiles/SelectedFiles';
import { TICKET_STATUS_TYPES } from '../../../pages/Tickets/TicketDetails/constants';

import './CommentSection.scss';

export default function CommentSection() {
  const queryClient = useQueryClient();
  const filesInputRef = useRef(null);
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);

  const {
    ticketResponseListing,
    ticketDetails,
    setDisplayModalOpen,
    setDisplayModalFile,
    downloadAttachmentMutation,
  } = useTicketDetails();

  const {
    mutate: createTicketResponseMutation,
    isPending: createTicketResponseIsPending,
  } = useMutation({
    mutationFn: payload => createTicketResponse(payload),
    onSuccess: () => {
      toast.success('Response added successfully.', {
        className: 'toast-success',
      });
      queryClient.invalidateQueries(['ticketResponseListing']);
      setSelectedFiles([]);
      if (filesInputRef.current) {
        filesInputRef.current.value = '';
      }
      setMessage('');
    },
    onError: error => {
      toast.error('Failed to add response. Please try again.', error, {
        className: 'toast-error',
      });
    },
  });

  const handleTicketResponseDownloadAttachment = async (tickResponse, doc) => {
    try {
      const response = await downloadAttachmentMutation.mutateAsync({
        ticketNo: ticketDetails?.TicketNo,
        fileName: doc,
        ticketResponseId: tickResponse?.TicketResponseId,
        type: 'response',
      });
      const blob = new Blob([response], { type: response.type });
      FileUtil.downloadBlobFile(
        blob,
        FileUtil.parseFileName(doc),
        response.type,
      );
    } catch (error) {
      toast.error('Failed to download attachment. Please try again.', {
        className: 'toast-error',
      });
    }
  };

  const handleTicketResponseDisplayAttachment = async (tickResponse, doc) => {
    try {
      const response = await downloadAttachmentMutation.mutateAsync({
        ticketNo: ticketDetails?.TicketNo,
        fileName: doc,
        ticketResponseId: tickResponse?.TicketResponseId,
        type: 'response',
      });
      const blob = new Blob([response], { type: response.type });
      const file = new File([blob], FileUtil.parseFileName(doc), {
        type: response.type,
      });
      setDisplayModalFile(file);
      setDisplayModalOpen(true);
    } catch (error) {
      toast.error('Failed to download attachment. Please try again.', {
        className: 'toast-error',
      });
    }
  };

  const handleAddResponse = () => {
    if (!message.trim() && selectedFiles.length === 0) {
      return;
    }
    const formData = new FormData();
    formData.append('ticketNo', ticketDetails?.TicketNo);
    formData.append('ticketResponse', message);
    if (selectedFiles.length > 0) {
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
    }
    createTicketResponseMutation(formData);
  };

  const handleFilesSelected = event => {
    const files = Array.from(event.target.files);
    // Files validation
    if (
      !FormValidationUtil.validateMultipleFilesSize(
        FileUtil.DEFAULT_CHAT_ATTACHMENT_SIZE_B,
      )(files)
    ) {
      toast.error(
        `File size must be less than ${FileUtil.DEFAULT_CHAT_ATTACHMENT_SIZE_MB}MB.`,
        {
          className: 'toast-error',
        },
      );
      return;
    }
    if (
      !FormValidationUtil.validateMultipleFilesFormat(
        FileUtil.DEFAULT_ALLOWED_ATTACHMENT_TYPES,
      )(files)
    ) {
      toast.error(
        FormValidationUtil.validateMultipleFilesFormatMessage(
          FileUtil.DEFAULT_ALLOWED_ATTACHMENT_TYPES,
        ),
        {
          className: 'toast-error',
        },
      );
      return;
    }

    if (files.length > 0) {
      setSelectedFiles(files);
    } else {
      setSelectedFiles([]);
    }
  };

  const handleDeleteFile = file => {
    setSelectedFiles(prevFiles =>
      prevFiles.filter(
        f =>
          `${f.name}-${f.lastModified}` !== `${file.name}-${file.lastModified}`,
      ),
    );
  };

  return (
    <div className="d-flex flex-column comments-col">
      <div className="comments-header p-3">Comments</div>
      <div className="p-3 comments-section flex-grow-1">
        <TicketResponseList
          ticketResponseListing={ticketResponseListing}
          handleTicketResponseDownloadAttachment={
            handleTicketResponseDownloadAttachment
          }
          handleTicketResponseDisplayAttachment={
            handleTicketResponseDisplayAttachment
          }
        />
      </div>
      <div className="message-input-container d-flex flex-column gap-3 p-3">
        <div className="d-flex flex-row align-items-center gap-2">
          <div className="input-wrapper">
            <Input
              placeholder="Type a new message"
              className="message-input"
              type="text"
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAddResponse();
                }
              }}
              disabled={
                createTicketResponseIsPending ||
                ticketDetails?.Status === TICKET_STATUS_TYPES.CANCELLED ||
                ticketDetails?.Status === TICKET_STATUS_TYPES.COMPLETED
              }
            />
            <button
              type="button"
              className="attach-button"
              title="Attach Document"
              onClick={() => filesInputRef.current.click()}
              disabled={
                createTicketResponseIsPending ||
                ticketDetails?.Status === TICKET_STATUS_TYPES.CANCELLED ||
                ticketDetails?.Status === TICKET_STATUS_TYPES.COMPLETED
              }
            >
              <img src={attachDocIcon} alt="Attach" />
            </button>
          </div>
          <button
            type="button"
            className="send-button"
            title="Send Message"
            onClick={handleAddResponse}
            disabled={
              createTicketResponseIsPending ||
              (!message && selectedFiles.length === 0) ||
              ticketDetails?.Status === TICKET_STATUS_TYPES.CANCELLED ||
              ticketDetails?.Status === TICKET_STATUS_TYPES.COMPLETED
            }
          >
            {createTicketResponseIsPending ? (
              <Spinner size="sm" color="white" />
            ) : (
              <img src={sendIcon} alt="Send" />
            )}
          </button>
        </div>
        {selectedFiles.length > 0 && (
          <div className="mt-2 selected-files-preview ">
            <SelectedFiles
              files={selectedFiles}
              handleDeleteFile={handleDeleteFile}
              display
              download
            />
          </div>
        )}
      </div>
      <input
        type="file"
        className="d-none"
        id="fileInput"
        multiple
        ref={filesInputRef}
        onChange={handleFilesSelected}
      />
    </div>
  );
}
